import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Target,
  Users,
  Zap,
  Calendar,
  Award,
  Activity,
  BarChart2,
  RefreshCw,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { User } from '../../types';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface PerformanceMetrics {
  overview: {
    totalTasks: number;
    completedTasks: number;
    completionRate: number;
    averageCompletionTime: number;
    totalTimeSpent: number;
    onTimeDelivery: number;
  };
  velocity: {
    weeklyData: Array<{
      week: string;
      completed: number;
      created: number;
      velocity: number;
    }>;
    trend: 'up' | 'down' | 'stable';
    averageVelocity: number;
  };
  teamPerformance: Array<{
    name: string;
    completed: number;
    inProgress: number;
    avgTime: number;
    efficiency: number;
  }>;
  timeDistribution: Array<{
    taskType: string;
    hours: number;
    count: number;
  }>;
  priorityDistribution: Array<{
    priority: string;
    count: number;
    percentage: number;
  }>;
  burndown: Array<{
    date: string;
    ideal: number;
    actual: number;
    remaining: number;
  }>;
  trendData: Array<{
    date: string;
    completed: number;
    created: number;
    resolved: number;
  }>;
}

interface PerformanceDashboardProps {
  currentUser?: User;
}

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
  orange: '#f97316'
};

const PRIORITY_COLORS = {
  low: COLORS.success,
  medium: COLORS.warning,
  high: COLORS.orange,
  critical: COLORS.danger
};

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ currentUser }) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days
  const [selectedTeamMember, setSelectedTeamMember] = useState('all');
  const [teamMembers, setTeamMembers] = useState<{ id: string; name: string }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetchPerformanceData();
  }, [timeRange, selectedTeamMember, currentUser]);

  useEffect(() => {
    // Populates the team-member filter dropdown — selectedTeamMember was already
    // being sent to the backend as `userId`, but nothing in the UI let a user set it.
    fetch('/api/users')
      .then(res => res.ok ? res.json() : [])
      .then(users => setTeamMembers((Array.isArray(users) ? users : []).map((u: any) => ({ id: u.id, name: u.name || u.username || u.email }))))
      .catch(() => setTeamMembers([]));
  }, []);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('days', timeRange);
      if (selectedTeamMember !== 'all') params.append('userId', selectedTeamMember);
      
      // Add current user context for role-based filtering
      if (currentUser) {
        params.append('contextUserId', currentUser.id);
        params.append('contextUserRole', currentUser.role);
      }

      const response = await fetch(`/api/tasks/performance/metrics?${params}`);
      if (!response.ok) {
        const message = await readApiError(response, 'Failed to load performance data');
        setLoadError(message);
        toast(message, 'error');
        return;
      }
      const data = await response.json();
      if (!data || typeof data !== 'object' || !data.overview) {
        setLoadError('Failed to load performance data');
        return;
      }
      setMetrics(data);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      setLoadError('Failed to load performance data');
      toast('Failed to load performance data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!metrics) return;
    const { overview, teamPerformance, timeDistribution, priorityDistribution } = metrics;

    const csvContent = [
      ['Metric', 'Value'],
      ['Total Tasks', overview.totalTasks],
      ['Completed Tasks', overview.completedTasks],
      ['Completion Rate (%)', overview.completionRate],
      ['On-Time Delivery (%)', overview.onTimeDelivery],
      ['Average Completion Time (hours)', overview.averageCompletionTime],
      ['Total Time Spent (hours)', overview.totalTimeSpent],
      [''],
      ['Team Member', 'Completed', 'In Progress', 'Avg. Time (h)', 'Efficiency (%)'],
      ...teamPerformance.map(m => [m.name, m.completed, m.inProgress, m.avgTime, m.efficiency]),
      [''],
      ['Task Type', 'Hours', 'Count'],
      ...timeDistribution.map(t => [t.taskType, t.hours, t.count]),
      [''],
      ['Priority', 'Count', 'Percentage (%)'],
      ...priorityDistribution.map(p => [p.priority, p.count, p.percentage])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{loadError || 'Failed to load performance data'}</p>
      </div>
    );
  }

  const { overview, velocity, teamPerformance, timeDistribution, priorityDistribution, burndown, trendData } = metrics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-7 h-7 text-blue-600" />
            Performance Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Team productivity and performance metrics</p>
        </div>
        
        {/* Filters */}
        <div className="flex gap-3">
          <select
            value={selectedTeamMember}
            onChange={(e) => setSelectedTeamMember(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Team Members</option>
            {teamMembers.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
          
          <button
            onClick={fetchPerformanceData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900">{overview.totalTasks}</p>
              <p className="text-sm text-green-600 mt-1">
                {overview.completedTasks} completed
              </p>
            </div>
            <Target className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completion Rate</p>
              <p className="text-3xl font-bold text-gray-900">{overview.completionRate}%</p>
              <p className="text-sm text-gray-500 mt-1">
                On-time: {overview.onTimeDelivery}%
              </p>
            </div>
            <Award className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        {/* Average Time */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Avg. Completion Time</p>
              <p className="text-3xl font-bold text-gray-900">{overview.averageCompletionTime}h</p>
              <p className="text-sm text-gray-500 mt-1">
                Total: {overview.totalTimeSpent}h
              </p>
            </div>
            <Clock className="w-12 h-12 text-orange-500 opacity-20" />
          </div>
        </div>

        {/* Velocity */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Avg. Velocity</p>
              <p className="text-3xl font-bold text-gray-900">{velocity.averageVelocity}</p>
              <div className="flex items-center gap-1 mt-1">
                {velocity.trend === 'up' ? (
                  <>
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600">Trending up</span>
                  </>
                ) : velocity.trend === 'down' ? (
                  <>
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-600">Trending down</span>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">Stable</span>
                )}
              </div>
            </div>
            <Zap className="w-12 h-12 text-purple-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Velocity Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Weekly Velocity
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={velocity.weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="completed" stroke={COLORS.success} strokeWidth={2} name="Completed" />
              <Line type="monotone" dataKey="created" stroke={COLORS.primary} strokeWidth={2} name="Created" />
              <Line type="monotone" dataKey="velocity" stroke={COLORS.purple} strokeWidth={2} name="Velocity" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Burndown Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-orange-600" />
            Burndown Chart
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={burndown}>
              <defs>
                <linearGradient id="colorIdeal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.1}/>
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="ideal" stroke={COLORS.primary} fillOpacity={1} fill="url(#colorIdeal)" name="Ideal" />
              <Area type="monotone" dataKey="actual" stroke={COLORS.success} fillOpacity={1} fill="url(#colorActual)" name="Actual" />
              <Line type="monotone" dataKey="remaining" stroke={COLORS.danger} strokeWidth={2} name="Remaining" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600" />
            Time Distribution by Task Type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={timeDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="taskType" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="hours" fill={COLORS.purple} name="Hours Spent" />
              <Bar dataKey="count" fill={COLORS.teal} name="Task Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Priority Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-600" />
            Priority Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={priorityDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.priority}: ${entry.percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {priorityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[entry.priority.toLowerCase() as keyof typeof PRIORITY_COLORS] || COLORS.primary} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Team Performance Table */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Team Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Team Member</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Completed</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">In Progress</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Avg. Time (h)</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Efficiency</th>
              </tr>
            </thead>
            <tbody>
              {teamPerformance.length > 0 ? (
                teamPerformance.map((member, index) => {
                  const memberName = member.name || 'Unknown User';
                  return (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {memberName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">{memberName}</span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {member.completed || 0}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {member.inProgress || 0}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4 text-gray-700">{member.avgTime || 0}</td>
                      <td className="text-center py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full"
                              style={{ width: `${Math.min(member.efficiency || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{member.efficiency || 0}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No team performance data available for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trend Analysis */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" />
          Task Trend Analysis
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="created" stroke={COLORS.primary} fillOpacity={1} fill="url(#colorCreated)" name="Created" />
            <Area type="monotone" dataKey="completed" stroke={COLORS.success} fillOpacity={1} fill="url(#colorCompleted)" name="Completed" />
            <Line type="monotone" dataKey="resolved" stroke={COLORS.orange} strokeWidth={2} name="Resolved" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
